import { useState } from 'react';
import { sendEvent, useEventNotification } from '../../features/visual-analytics/hooks/events';
import { ModalPortal } from '../modal-portal/ModalPortal';

import styles from './yamlEditor.module.scss';
import YamlEditor from './YamlEditor';
import YamlSchemaDocumentation from './YamlSchemaDocumentation';
import { verifyYaml } from '../jsonSchemaValidator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const MSGYamlModal = 'showYamlModal';

interface TpYamlModalData {
  initialYamlString: string;
  yamlSchema?: any;
  callBackOK: any;
  callBackCancel: any;
}

export function promptYamlModal(initialYamlString: string, yamlSchema?: any) {
  return new Promise<string>((resolve, reject) => {
    function callBackOK(data: string) {
      resolve(data);
    }

    function callBackCancel() {
      reject();
    }

    const confirmYamlData: TpYamlModalData = {
      initialYamlString,
      yamlSchema,
      callBackOK,
      callBackCancel,
    };
    sendEvent(MSGYamlModal, confirmYamlData);
  });
}

export function YamlModal() {
  const [activeYaml, setActiveYaml] = useState<TpYamlModalData | null>(null);
  const [content, setContent] = useState('');
  const [verified, setVerified] = useState(false);
  const [yamlSchema, setYamlSchema] = useState<any>(null);
  const [verificationError, setVerificationError] = useState<any>(null);

  useEventNotification(
    MSGYamlModal,
    (tpe: string, contentInput: TpYamlModalData) => {
      setActiveYaml(contentInput);
      setContent(contentInput.initialYamlString);
      setYamlSchema(contentInput.yamlSchema);
      setVerified(false);
      setVerificationError(null);
    },
    [],
  );

  if (!activeYaml) return null;

  const hasSidePanel = yamlSchema || verificationError;

  return (
    <ModalPortal
      close={() => {
        setActiveYaml(null);
      }}
    >
      <div className={styles.YamlModalWrapper}>
        <div className={styles.yamlModalBodyWrapper}>
          <div className={styles.yamlEditorWrapper} style={{ width: !hasSidePanel ? '100%' : '50%' }}>
            <YamlEditor
              initialContent={content}
              contentModified={(newContent) => {
                setVerified(false);
                setContent(newContent);
              }}
            />
          </div>

          {hasSidePanel && (
            <div className={styles.sidePanelWrapper}>
              {verificationError && <div style={{ width: '100%', height: '50%' }}>{verificationError}</div>}
              {yamlSchema && (
                <div style={{ width: '100%', height: verificationError ? '50%' : '100%' }}>
                  <YamlSchemaDocumentation yamlSchema={yamlSchema} />
                </div>
              )}
            </div>
          )}
        </div>
        <div className={styles.buttonSection}>
          <button
            onClick={() => {
              const verificationError = verifyYaml(content, yamlSchema);
              if (!verificationError) {
                setVerified(true);
              } else {
                setVerificationError(verificationError);
                setVerified(false);
              }
            }}
          >
            {!verified && <span>Verify</span>}
            {verified && (
              <span>
                Verified&nbsp;
                <FontAwesomeIcon icon="check" />
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setActiveYaml(null);
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const verificationError = verifyYaml(content, yamlSchema);
              if (!verificationError) {
                setVerified(true);
                activeYaml.callBackOK(content);
                setActiveYaml(null);
              } else {
                setVerificationError(verificationError);
                setVerified(false);
              }
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}
